import { useCallback } from 'react';
import axios from 'axios';
import { getToken } from '../../../services/tokenService';
import { normalizePhoneForForm } from '../utils/phoneUtils';

const API = '/api';

// Date format helper: DD/MM/YYYY -> YYYY-MM-DD for HTML date inputs
const formatDateForInput = (d) => {
  if (!d || !d.includes('/')) return d || '';
  const parts = d.split('/');
  return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : d;
};

// Normalize Code APE/NAF: "43.21A " -> "4321A"
const formatCodeNaf = (v) => v ? v.replace(/[\s.]/g, '').toUpperCase() : '';

export default function useMemberSelection({ membresAdherents, setSelectedClient, setFormData, sig }) {
  const handleSelectMembre = useCallback(async (membreId) => {
    setSelectedClient(membreId);
    if (membreId === 'nouveau') {
      sig.setIsFtpeMember(false);
      sig.setFtpeStatus('pending');
      sig.setFtpeAccepted(false);
      setFormData(prev => ({
        ...prev,
        membre_id: '', raison_sociale: '', siret: '', code_naf: '', forme_juridique: '',
        nom_gerant: '', prenom_gerant: '', fonction_gerant: 'Gerant',
        adresse_entreprise: '', code_postal: '', ville: '', pays: 'FR',
        territoire: 'metropole', territoires: [], territoires_ca: {},
        indicatif_telephone: '+33', telephone: '',
        email: '', site_web: '', effectif: '', date_creation_entreprise: ''
      }));
    } else {
      const membre = membresAdherents.find(m => m.id === membreId);
      if (membre) {
        try {
          const ftpeRes = await axios.get(`${API}/ftpe-adhesion/member/${membre.id}/ftpe-status`, {
            headers: { Authorization: `Bearer ${getToken()}` }
          });
          // Strict : ne considère 'FTPE membre' que si has_ftpe===true ET
          // status==='adherent'. Aligné avec PropositionsESSSection
          // (afficher +200€ tant que l'adhésion n'est pas finalisée).
          let isAdherent = ftpeRes.data?.has_ftpe === true && ftpeRes.data?.status === 'adherent';
          // Devis émis pour un souscripteur « déjà adhérent » (réel ou déclaré par le super admin) : pas de FTPE
          const devisId = new URLSearchParams(window.location.search).get('proposition');
          if (!isAdherent && devisId) {
            try {
              const st = await axios.get(`${API}/souscriptions/simulateur/ftpe-statut/${encodeURIComponent(devisId)}`, {
                headers: { Authorization: `Bearer ${getToken()}` },
              });
              isAdherent = st.data?.deja_adherent === true;
            } catch { /* statut inconnu : comportement standard */ }
          }
          sig.setIsFtpeMember(isAdherent);
          if (isAdherent) { sig.setFtpeAccepted(true); sig.setFtpeStatus('paid'); }
        } catch { sig.setIsFtpeMember(false); }
        setFormData(prev => {
          // Normalisation du téléphone : si le membre a stocké son numéro avec
          // l'indicatif inclus (+590690…), on le splitte pour ne pas afficher
          // deux fois le préfixe (l'indicatif est déjà rendu par le composant
          // PhoneInputWithFlagSVG dans le formulaire).
          const phoneNormalized = normalizePhoneForForm(
            membre.telephone || membre.phone || '',
            membre.indicatif_telephone || prev.indicatif_telephone || '+33',
          );
          return {
          ...prev,
          membre_id: membre.id,
          raison_sociale: membre.raison_sociale || membre.company_name || membre.entreprise || '',
          siret: membre.siret || '', code_naf: formatCodeNaf(membre.code_naf || ''),
          forme_juridique: membre.forme_juridique || membre.statut_juridique || '',
          date_creation_entreprise: formatDateForInput(membre.date_creation_entreprise || ''),
          effectif: membre.effectif || '',
          nom_gerant: membre.nom_gerant || membre.nom || '',
          prenom_gerant: membre.prenom_gerant || membre.prenom || '',
          fonction_gerant: membre.fonction_gerant || 'Gerant',
          adresse_entreprise: membre.adresse_entreprise || membre.adresse || membre.address || '',
          code_postal: membre.code_postal || '', ville: membre.ville || membre.city || '',
          pays: membre.pays || 'FR', territoire: membre.territoire || 'metropole',
          // V36 — pré-remplissage multi-territoires depuis le membre (peut être déjà saisi via simulateur)
          territoires: Array.isArray(membre.territoires) && membre.territoires.length > 0
            ? membre.territoires
            : (Array.isArray(prev.territoires) && prev.territoires.length > 0
                ? prev.territoires
                : (membre.territoire ? [membre.territoire] : (prev.territoire ? [prev.territoire] : []))),
          territoires_ca: (membre.territoires_ca && typeof membre.territoires_ca === 'object')
            ? membre.territoires_ca
            : (prev.territoires_ca || {}),
          indicatif_telephone: phoneNormalized.indicatif_telephone,
          telephone: phoneNormalized.telephone,
          email: membre.email || '', site_web: membre.site_web || '',
          chiffre_affaires: membre.chiffre_affaires || prev.chiffre_affaires || 0,
        };
        });
      }
    }
  }, [membresAdherents, setSelectedClient, setFormData, sig]);

  return { handleSelectMembre, formatDateForInput, formatCodeNaf };
}
